import type { ContentStatus } from './enums.js';

/**
 * The editorial status machine from dokumentasjon/03-datamodell.md.
 *
 *   SUGGESTED ──approve──▶ APPROVED ──draft written──▶ DRAFT
 *       └──reject──▶ REJECTED                            │
 *       └────────ask Nova / write draft────────────▶ DRAFT
 *                                                    schedule
 *                                                        ▼
 *   PUBLISHED ◀──confirm── SCHEDULED
 *
 * «Utkast» and «Klar til planlegging» were merged into one «I arbeid» status
 * (DRAFT) — a card is worked on until it is scheduled straight for publishing.
 *
 * Anything still alive can be cancelled, and anything still alive can be sent
 * back to the start (SUGGESTED) — the draft it carries is kept, only the status
 * resets. Landing in DRAFT needs an actual draft (unless Nova is writing it),
 * and SCHEDULED needs real content — the table says the move is *shaped* right,
 * `contentGateFor` says it is *ready*. The API enforces both; the web app uses
 * them to decide which drag-and-drop moves and buttons are legal, and to explain
 * a blocked one instead of failing silently.
 */
export const CONTENT_TRANSITIONS: Record<ContentStatus, readonly ContentStatus[]> = {
  // Pre-forslag: the area owner either accepts it onto the board (→ SUGGESTED)
  // or discards it (→ REJECTED). It can also age out (EXPIRED) or be cancelled.
  PROPOSED: ['SUGGESTED', 'REJECTED', 'EXPIRED', 'CANCELLED'],
  SUGGESTED: ['APPROVED', 'DRAFT', 'REJECTED', 'CANCELLED', 'EXPIRED'],
  APPROVED: ['DRAFT', 'SUGGESTED', 'CANCELLED'],
  DRAFT: ['SCHEDULED', 'APPROVED', 'SUGGESTED', 'CANCELLED'],
  SCHEDULED: ['PUBLISHED', 'DRAFT', 'SUGGESTED', 'CANCELLED'],
  PUBLISHED: [],
  REJECTED: ['SUGGESTED'],
  CANCELLED: ['SUGGESTED'],
  EXPIRED: ['SUGGESTED'],
};

export function canTransition(from: ContentStatus, to: ContentStatus): boolean {
  return CONTENT_TRANSITIONS[from].includes(to);
}

/** Reasons a table-legal move is still blocked until a draft exists. */
export type ContentGate = 'NEEDS_DRAFT' | 'NEEDS_CONTENT';

/**
 * A move can pass the status table yet still be wrong: a card cannot land in
 * «I arbeid» with nothing written — unless Nova is already writing it — and it
 * can never be scheduled for publishing without real text. Returns the reason to
 * block, or null when the move may proceed.
 */
export function contentGateFor(
  to: ContentStatus,
  state: { hasContent: boolean; draftRequested: boolean },
): ContentGate | null {
  if (to === 'DRAFT' && !state.hasContent && !state.draftRequested) return 'NEEDS_DRAFT';
  if (to === 'SCHEDULED' && !state.hasContent) return 'NEEDS_CONTENT';
  return null;
}

/**
 * FORSINKET ("overdue") is derived, never stored: content that still needs a
 * draft after its deadline, or that was scheduled for a moment now past.
 */
export function isOverdue(
  item: {
    status: ContentStatus;
    draftDeadline?: Date | string | null;
    publishAt?: Date | string | null;
  },
  now: Date = new Date(),
): boolean {
  const deadline = item.draftDeadline ? new Date(item.draftDeadline) : null;
  const publishAt = item.publishAt ? new Date(item.publishAt) : null;

  // A proposal (SUGGESTED) is an unconfirmed idea — nobody committed to it, so it
  // is never «forsinket». Only ordered/drafted work with a deadline can be late.
  if (deadline && now > deadline && ['APPROVED', 'DRAFT'].includes(item.status)) {
    return true;
  }
  if (publishAt && now > publishAt && item.status === 'SCHEDULED') {
    return true;
  }
  return false;
}

/** Statuses an agent may set on its own — agents never publish (04-agenter.md). */
export const AGENT_ALLOWED_STATUSES: readonly ContentStatus[] = ['SUGGESTED', 'DRAFT'];

/**
 * The status a freshly created content item starts in. An agent's idea lands as
 * a hidden pre-forslag (PROPOSED) that waits for the owner's review; a human
 * files straight onto the board (SUGGESTED) — they already know they want it.
 */
export function initialContentStatus(fromAgent: boolean): ContentStatus {
  return fromAgent ? 'PROPOSED' : 'SUGGESTED';
}

/**
 * Reindex a within-column drag to stable, ascending `boardOrder` values. Given
 * the full ordered list of card ids in one status column, each card is assigned
 * its position as its order value — simple and robust, and the result always
 * sorts back into exactly the order given (redplan-ux fix #5).
 */
export function boardOrderValues(
  orderedIds: readonly string[],
): { id: string; boardOrder: number }[] {
  return orderedIds.map((id, index) => ({ id, boardOrder: index }));
}

/**
 * Board sort comparator: placed cards (a `boardOrder` is set) come first in
 * ascending order; unplaced cards (null) keep their incoming order and fall to
 * the end. Used only by the board — other views keep their date ordering.
 */
export function compareBoardOrder(
  a: { boardOrder: number | null },
  b: { boardOrder: number | null },
): number {
  if (a.boardOrder === null && b.boardOrder === null) return 0;
  if (a.boardOrder === null) return 1;
  if (b.boardOrder === null) return -1;
  return a.boardOrder - b.boardOrder;
}
